import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '@/api/client';
import { walletApi } from '@/api/wallet';
import { stationsApi, type StationDetailOut } from '@/api/stations';
import { chargingApi } from '@/api/charging';
import { amenitiesApi, type AmenitySessionOut } from '@/api/amenities';
import { getMe, type MeOut } from '@/api/alianca';
import { useAuthStore } from '@/store/auth';
import { useWalletStore } from '@/store/wallet';
import { useSessionStore } from '@/store/session';
import { ActiveSessionAlert } from '@/components/home/ActiveSessionAlert';
import { ActiveBannerFloat } from '@/components/home/ActiveBannerFloat';
import { ActiveVacuumAlert } from '@/components/home/ActiveVacuumAlert';
import { HomeStationList } from '@/components/home/HomeStationList';
import { HomeStationSheet, type SheetState } from '@/components/home/HomeStationSheet';
import { StationMap } from '@/components/map/StationMap';
import { InstallPrompt } from '@/components/profile/InstallPrompt';
import { LocationNudge } from '@/components/location/LocationNudge';
import { Button } from '@/components/ui/Button';
import { Display, Eyebrow } from '@/components/ui/Typography';
import { useUserPosition } from '@/hooks/useUserPosition';
import { useAppConfig } from '@/lib/config';
import { haversineKm } from '@/lib/geo';
import { countAvailableConnectors, isStationOffline } from '@/lib/stationStats';

type LoadState = 'loading' | 'ready' | 'error';

// Polling silencioso pra manter a contagem "X livres" fresca enquanto o
// Hub Gateway não tem push WS pra lista. Mesmo padrão do StationDetail
// (5s por estação), aqui 10s por ser uma lista agregada — suficiente pro
// usuário ver mudança antes de tocar na estação.
const STATIONS_POLL_MS = 10_000;

function greeting(): string {
  const h = new Date().getHours();
  if (h < 6) return 'Boa madrugada';
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

function initialsOf(name: string | undefined): string {
  if (!name) return 'B';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'B';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export default function Home() {
  const user = useAuthStore((s) => s.user);
  // Saldo saiu da Home (feedback Saulo 2026-07-02 — mora só na aba Saldo),
  // mas o fetch continua aquecendo o store pra outras telas.
  const setWallet = useWalletStore((s) => s.setWallet);
  const setActive = useSessionStore((s) => s.setActive);
  const active = useSessionStore((s) => s.active);
  const { google_maps_api_key } = useAppConfig();

  const [stations, setStations] = useState<StationDetailOut[]>([]);
  const [status, setStatus] = useState<LoadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [aliancaMe, setAliancaMe] = useState<MeOut | null>(null);
  // Aspirador em andamento — estado local (não tem store, ao contrário da
  // recarga). Desde a migration 050 dá pra ter recarga E aspirador rodando
  // juntos, então os dois banners convivem.
  const [activeVacuum, setActiveVacuum] = useState<AmenitySessionOut | null>(null);

  // Map-first (2026-07-05): mapa é o padrão; "Ver lista" fica como atalho pra
  // quem prefere a lista clássica, ou é o único modo quando não há chave do
  // Google Maps configurada (fallback gracioso, sem crash).
  const mapAvailable = !!google_maps_api_key;
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map');
  const [sheetState, setSheetState] = useState<SheetState>({ mode: 'peek' });

  // Estado da Aliança — fetch próprio (não bloqueia wallet/estações). Só serve
  // pra decidir se mostramos o card de captação (não-membro). Falha → sem card.
  useEffect(() => {
    getMe()
      .then(setAliancaMe)
      .catch(() => setAliancaMe(null));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError(null);

    (async () => {
      try {
        const [walletData, stationsList, activeSession, activeVac] = await Promise.all([
          walletApi.get(),
          stationsApi.listDetailed(),
          chargingApi.active(),
          // Falha aqui não derruba a home — o aspirador é acessório.
          amenitiesApi.activeSession().catch(() => null)
        ]);
        if (cancelled) return;
        setWallet(walletData);
        setActive(activeSession);
        setActiveVacuum(activeVac);
        setStations(stationsList);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Falha ao carregar');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reloadTick, setWallet, setActive]);

  // Refresh silencioso da lista de estações — mantém o status dos
  // conectores vivo sem precisar pull-to-refresh. Não toca em wallet (tem seu
  // próprio canal) e nunca volta pra `loading`, pra não piscar skeleton: só
  // promove pra `ready` quando a lista chega, que é como a tela se recupera
  // sozinha depois de uma falha de rede. Também não deve mexer no mapa (o hook
  // de bounds só reage a transições reais de carregamento/geolocalização, não
  // a este tick).
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      stationsApi
        .listDetailed()
        .then((list) => {
          if (cancelled) return;
          setStations(list);
          // Auto-recuperação. Sem isto o `status` só era decidido no mount: a
          // Home que ficasse presa em `loading` (request pendurada) ou em
          // `error` continuava assim pra sempre — mesmo com a rede de volta e
          // com esta lista chegando a cada 10s — até o app remontar. Era
          // metade do "fica sempre nessa tela" do cliente de 2026-09-05.
          setStatus('ready');
          setError(null);
        })
        .catch(() => {
          // erro silencioso — próximo tick tenta de novo; se a request
          // inicial falhar, o usuário já vê o estado de erro do bloco acima
        });
      // Sessões vivas no mesmo tick. Antes só eram buscadas no mount, então o
      // banner de recarga ficava no ar depois da sessão acabar até o app
      // remontar; com o bloco do aspirador (5-20 min) isso seria pior ainda.
      chargingApi
        .active()
        .then((s) => {
          if (!cancelled) setActive(s);
        })
        .catch(() => {
          /* silencioso — mantém o último estado */
        });
      amenitiesApi
        .activeSession()
        .then((s) => {
          if (!cancelled) setActiveVacuum(s);
        })
        .catch(() => {
          /* silencioso */
        });
    };
    // Volta do background ou rede de volta: refaz o tick na hora, sem esperar
    // os 10s do intervalo. `visibilitychange` vale no PWA e no WebView do
    // Capacitor (que fica hidden em background), então não depende do plugin
    // @capacitor/app — ausente nos shells 1.1.x (ver lib/deepLinks.ts).
    const onWake = () => {
      if (document.visibilityState === 'visible') tick();
    };
    const id = window.setInterval(tick, STATIONS_POLL_MS);
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('online', onWake);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('online', onWake);
    };
  }, [reloadTick]);

  const firstName = user?.name?.trim().split(/\s+/)[0] ?? '';

  // "Estação mais próxima" (feedback cliente nº 7): ordenação 100% client-side
  // pela posição do usuário — a coordenada nunca sai do aparelho. Sem posição
  // (negada/indisponível), mantém a ordem do backend.
  const {
    coords,
    status: geoStatus,
    locating,
    request: requestPosition
  } = useUserPosition();

  // Espera a geolocalização resolver ANTES de montar o mapa, pra ele já abrir
  // enquadrado direto na posição certa em vez de aparecer no centro genérico
  // (Vitória/ES) e só depois "pular"/dar zoom pra onde o usuário está —
  // some o salto visual perceptível ao abrir a Home (feedback Saulo 2026-07-06).
  // Timeout de segurança: consulta ou leitura que não responde (GPS sem sinal)
  // não pode travar o mapa pra sempre — usa o fallback depois.
  const geoPending =
    geoStatus === 'checking' || (geoStatus === 'granted' && coords === null && locating);
  const [geoTimedOut, setGeoTimedOut] = useState(false);
  useEffect(() => {
    if (!geoPending) return;
    const id = window.setTimeout(() => setGeoTimedOut(true), 2000);
    return () => window.clearTimeout(id);
  }, [geoPending]);
  const geoSettled = !geoPending || geoTimedOut;

  // Sem posição, a Home NÃO aponta "o mais próximo" (seria só o 1º da lista do
  // backend — era assim que cliente caía no eletroposto errado) e insiste na
  // localização a cada abertura, sem botão de fechar (pedido Saulo 2026-10-08).
  const awaitingFix =
    coords === null && (geoStatus === 'checking' || (geoStatus === 'granted' && locating));
  const locationNudge =
    coords === null && !awaitingFix && geoStatus !== 'unavailable' ? (
      <LocationNudge status={geoStatus} locating={locating} onRequest={requestPosition} />
    ) : null;
  const peekOverride =
    coords !== null ? undefined : awaitingFix ? (
      <div className="h-[72px] animate-pulse rounded-[18px] bg-bow-cream-2/60" />
    ) : (
      (locationNudge ?? (
        // Shell antigo sem o plugin / navegador sem geolocalização: não dá pra
        // saber onde o cliente está, então ele escolhe — sem "mais próximo".
        <p className="px-1 pb-1 text-[13.5px] leading-snug text-bow-royal/70">
          Toque no eletroposto onde você está, no mapa ou em{' '}
          <button
            type="button"
            onClick={() => setViewMode('list')}
            className="font-semibold text-bow-royal underline underline-offset-2"
          >
            Ver lista
          </button>
          .
        </p>
      ))
    );

  const { sorted, distances } = useMemo(() => {
    if (!coords) {
      return { sorted: stations, distances: new Map<string, number>() };
    }
    const distances = new Map<string, number>();
    for (const s of stations) {
      const lat = Number(s.latitude);
      const lng = Number(s.longitude);
      if (
        s.latitude !== null &&
        s.longitude !== null &&
        Number.isFinite(lat) &&
        Number.isFinite(lng)
      ) {
        distances.set(s.id, haversineKm(coords.lat, coords.lng, lat, lng));
      }
    }
    // Estações sem coordenada mantêm a posição relativa, depois das medidas.
    const sorted = [...stations].sort((a, b) => {
      const da = distances.get(a.id);
      const db = distances.get(b.id);
      if (da === undefined && db === undefined) return 0;
      if (da === undefined) return 1;
      if (db === undefined) return -1;
      return da - db;
    });
    return { sorted, distances };
  }, [stations, coords]);

  const nearestStation = sorted[0] ?? null;
  const nearestDistanceKm = nearestStation
    ? (distances.get(nearestStation.id) ?? null)
    : null;
  // Sugestão quando a mais próxima está fora do ar (Home Map-First.html ·
  // artboard "Home · peek · fora do ar"): a próxima estação operacional da
  // lista ordenada por distância, preferindo quem tem conector livre.
  const fallbackStation = useMemo(() => {
    if (!nearestStation || !isStationOffline(nearestStation)) return null;
    const rest = sorted.filter(
      (s) => s.id !== nearestStation.id && !isStationOffline(s)
    );
    return (
      rest.find((s) => countAvailableConnectors(s).available > 0) ?? rest[0] ?? null
    );
  }, [sorted, nearestStation]);
  const fallbackDistanceKm = fallbackStation
    ? (distances.get(fallbackStation.id) ?? null)
    : null;
  const selectedStation =
    sheetState.mode === 'selected'
      ? (sorted.find((s) => s.id === sheetState.stationId) ?? null)
      : null;
  const selectedDistanceKm = selectedStation
    ? (distances.get(selectedStation.id) ?? null)
    : null;
  const showAliancaCaptacao = !!aliancaMe?.adesao_disponivel;
  const showMap = mapAvailable && viewMode === 'map';
  // Só monta o `<Map>` quando já sabe o enquadramento inicial certo (estações
  // carregadas + geolocalização resolvida) — ver comentário do `geoSettled`.
  const mapReady = status === 'ready' && geoSettled;

  // NSS (design/no-scroll/nss-home.jsx · HomeNS): viewport fixa. No modo lista,
  // a lista de estações é o ÚNICO bloco flexível — rola internamente (§5.4 do
  // brief). No modo mapa, o mapa preenche tudo e a sheet flutua por cima.
  return (
    <div
      className="-mx-4 -mb-24 flex flex-col overflow-hidden"
      style={{
        marginTop: 'calc(-1 * max(1rem, env(safe-area-inset-top)))',
        height:
          'calc(100dvh - 62px - max(0.75rem, env(safe-area-inset-bottom)))',
        // Defesa extra além do `overscroll-behavior: none` global (globals.css)
        // — contém o gesto de arrastar o mapa pra não vazar bounce-scroll pra
        // fora deste frame (feedback Saulo 2026-07-08).
        overscrollBehavior: 'contain'
      }}
    >
      {showMap ? (
        <div className="relative flex-1">
          {mapReady ? (
            <StationMap
              apiKey={google_maps_api_key!}
              stations={sorted}
              userCoords={coords}
              selectedStationId={
                sheetState.mode === 'selected' ? sheetState.stationId : null
              }
              nearestStationId={nearestStation?.id ?? null}
              sheetExpanded={sheetState.mode === 'selected'}
              onSelectStation={(id) => setSheetState({ mode: 'selected', stationId: id })}
            />
          ) : (
            // Placeholder enquanto aguarda geolocalização resolver (ou o
            // timeout de segurança) — evita montar o mapa no centro genérico
            // e "pular" pra posição certa assim que a geo chega.
            <div className="absolute inset-0 flex items-center justify-center bg-bow-cream-2">
              <span className="h-3 w-3 animate-pulse rounded-full bg-bow-royal/30" />
            </div>
          )}

          {/* Overlay de topo — banner de sessão ativa + atalho "Ver lista",
              empilhados num flex-col pra nunca colidir (sem offsets manuais). */}
          <div
            className="pointer-events-none absolute inset-x-4 z-20 flex flex-col items-stretch gap-2"
            style={{ top: 'max(1rem, env(safe-area-inset-top))' }}
          >
            {active && <ActiveBannerFloat session={active} />}
            {activeVacuum && (
              <div className="pointer-events-auto">
                <ActiveVacuumAlert session={activeVacuum} />
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className="pointer-events-auto inline-flex h-10 items-center gap-1.5 rounded-full bg-white px-4 text-[13px] font-semibold text-bow-royal"
                style={{ boxShadow: '0 4px 14px rgba(0,27,116,0.14)' }}
              >
                <ListIcon />
                Ver lista
              </button>
            </div>
          </div>

          {status === 'ready' && (
            <HomeStationSheet
              sheetState={sheetState}
              nearestStation={nearestStation}
              nearestDistanceKm={nearestDistanceKm}
              selectedStation={selectedStation}
              selectedDistanceKm={selectedDistanceKm}
              fallbackStation={fallbackStation}
              fallbackDistanceKm={fallbackDistanceKm}
              onExpand={(id) => setSheetState({ mode: 'selected', stationId: id })}
              onCollapse={() => setSheetState({ mode: 'peek' })}
              showAliancaCaptacao={showAliancaCaptacao}
              peekOverride={peekOverride}
            />
          )}

          {status !== 'ready' && (
            <div
              className="absolute inset-x-0 bottom-0 rounded-t-[26px] bg-white p-5"
              style={{ boxShadow: '0 -10px 34px rgba(0,27,116,0.14)' }}
            >
              {status === 'loading' && (
                <div className="h-16 animate-pulse rounded-2xl bg-bow-royal/5" />
              )}
              {status === 'error' && (
                <div className="flex flex-col items-start gap-3">
                  <p className="text-sm text-bow-red">{error}</p>
                  <Button
                    onClick={() => setReloadTick((t) => t + 1)}
                    fullWidth={false}
                    size="md"
                  >
                    Tentar novamente
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div
          className="flex min-h-0 flex-1 flex-col gap-4 px-4 pb-2.5 [@media(max-height:740px)]:gap-3"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        >
          <InstallPrompt />

          {/* Header — saudação à esquerda, atalho de mapa + avatar à direita */}
          <header className="flex flex-shrink-0 items-center justify-between pt-1">
            <div>
              <Eyebrow>{greeting()}</Eyebrow>
              <Display size={26} className="mt-0.5 [@media(max-height:740px)]:!text-[23px]">
                {firstName || 'Bem-vinda'}
              </Display>
            </div>
            <div className="flex items-center gap-3">
              {mapAvailable && (
                <button
                  type="button"
                  onClick={() => setViewMode('map')}
                  className="text-[12.5px] font-semibold text-bow-royal"
                >
                  🗺️ Ver mapa
                </button>
              )}
              <div
                className="flex h-11 w-11 items-center justify-center rounded-full text-white"
                style={{
                  background: 'linear-gradient(135deg, #00C072 0%, #31B700 100%)',
                  boxShadow: '0 6px 16px rgba(0,192,114,0.35)',
                  fontFamily: 'Chillax, system-ui, sans-serif',
                  fontWeight: 600,
                  fontSize: 14
                }}
              >
                {initialsOf(user?.name)}
              </div>
            </div>
          </header>

          {/* Divisória — restaura a separação visual que o card de saldo fazia
              entre a saudação e a lista (design Saulo, nss-home.jsx v2) */}
          <div
            className="h-px flex-shrink-0"
            style={{ background: 'rgba(0,27,116,0.1)' }}
          />

          {active && (
            <div className="flex-shrink-0">
              <ActiveSessionAlert session={active} />
            </div>
          )}

          {activeVacuum && (
            <div className="flex-shrink-0">
              <ActiveVacuumAlert session={activeVacuum} />
            </div>
          )}

          <HomeStationList
            stations={sorted}
            status={status}
            error={error}
            onRetry={() => setReloadTick((t) => t + 1)}
            distances={distances}
            locationNudge={status === 'ready' ? locationNudge : null}
            showAliancaCaptacao={showAliancaCaptacao}
          />
        </div>
      )}
    </div>
  );
}

function ListIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}
